import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { LangChangeEvent, TranslateService } from '@ngx-translate/core';
import {
  PriceService,
  GungFlowService,
  AuthService,
  MetadataService,
  LocationConfigService,
  PriceConfigService,
  Availability,
  CustomerProductPrice,
  Product
} from 'gung-standard';
import { JeevesProductTableComponent, ProductTableRow } from 'gung-standard-jeeves';
import { Observable, of, forkJoin, Subscription } from 'rxjs';
import { first } from 'rxjs';
import { ColmecCustomerProductPrice } from '../../models/colmec-price';
import { ColmecJeevesProductTableComponent } from '../product/colmec-jeeves-product-table/colmec-jeeves-product-table.component';

@Component({
    selector: 'colmec-concept-products-table',
    templateUrl: './colmec-concept-products-table.component.html',
    styleUrls: ['./colmec-concept-products-table.component.scss'],
    standalone: false
})
export class ColmecConceptProductsTableComponent extends JeevesProductTableComponent implements OnInit, OnDestroy {
  private langChangeEvent: Subscription;
  public hidePrice = false;

  constructor(
    protected priceService: PriceService,
    protected gungFlowService: GungFlowService,
    protected authService: AuthService,
    private translateService: TranslateService,
    protected metadataService: MetadataService,
    protected router: Router,
    protected locationConfigService: LocationConfigService,
    private priceConfigService: PriceConfigService
  ) {
    // super(priceService, gungFlowService, authService, translateService, metadataService, router, locationConfigService, priceConfigService);
    super(priceService, gungFlowService, authService, router, locationConfigService);
  }

  goToDetail(product) {
    super.goToDetail(product);
  }

  ngOnInit() {
    super.ngOnInit();

    this.langChangeEvent = this.translateService.onLangChange.subscribe((event: LangChangeEvent) => {
      // User Change Language
      super.subscribeToProducts();
    });

    this.priceConfigService.hidePrice.subscribe(hidePrice => (this.hidePrice = hidePrice));
  }

  ngOnDestroy() {
    super.ngOnDestroy();
    if (this.langChangeEvent) {
      this.langChangeEvent.unsubscribe();
    }
  }

  getNetPriceLabel(product: Product): Observable<string> {
    if (!product) {
      return of('');
    }

    if (product.extra.ar.varugruppkod === '10') {
      return of(this.translateService.instant('NET_PRICE_INC_FRAME'));
    }
    return of(this.translateService.instant('NET_PRICE'));
  }

  getNetPriceValue(price: ColmecCustomerProductPrice): number {
    if (price.priceInclAutoAdded) {
      return price.priceInclAutoAdded.value;
    }

    return price.customerNetPrice.value;
  }

  getProductDescription(product: Product): Observable<string> {
    const lang = this.translateService.currentLang || 'se';
    const limit = 234;
    const trail = '...';

    let description = '';
    if (product.extra && product.extra.i18n && product.extra.i18n[lang]) {
      description = product.extra.i18n[lang].description || '';

      if (description.length > limit) {
        // Truncate the description
        description = description.substring(0, limit) + trail;
      }
    }
    return of(description);
  }

  getProductSubTitle(product: Product): Observable<string> {
    const lang = this.translateService.currentLang || 'se';

    let subTitle = '';
    if (product.extra && product.extra.i18n && product.extra.i18n[lang]) {
      subTitle = product.extra.i18n[lang].subTitle || '';
    }
    return of(subTitle);
  }

  getOptionName(table: string, value: string, key: string): string {
    const result = this.metadataService.getMetadataValue(table, value, key);
    if (result) {
      return result;
    }
    return '';
  }

  // goToDetail(product: Product): void {
  //   const layout = this.locationConfigService.getLayout('current-layout');
  //   this.locationConfigService.addLayout({
  //     path: 'products', // Save the current layout when click layout
  //     layoutIndex: layout && layout.layoutIndex || 0
  //   });
  //   this.locationConfigService.removeLayout(layout);
  //   super.goToDetail(product);
  // }

  protected subscribeToProducts() {
    if (!this.oldFlow || !this.currentFlow || this.oldFlow.id !== this.currentFlow.id) {
      // remove cached data;
      this.keyedMapData = {};
      this.oldFlow = this.currentFlow;
    }

    const sorted = this.data.sort((a, b) => {
      if (a.extra.availabilities[0].currentStock > b.extra.availabilities[0].currentStock) {
        return -1;
      }
      if (a.extra.availabilities[0].currentStock < b.extra.availabilities[0].currentStock) {
        return 1;
      }
      return 0;
    });

    const newIds = sorted.map(p => p.id).filter(id => !Object.keys(this.keyedMapData).includes(id));
    const newIdsWithouConcept = sorted
      .filter(p => p.productType === 'singleDimension')
      .map(p => p.id)
      .filter(id => !Object.keys(this.keyedMapData).includes(id));
    if (newIds.length === 0) {
      this.readMappedDataFromCache();
      return;
    }
    const subscription = forkJoin([
      of(sorted),
      this.priceService.getCurrentCustomerPrices(newIdsWithouConcept).pipe(first()),
      this.gungFlowService.getSelectedFlow().pipe(first())
    ])
      .pipe(first())
      .subscribe(data => {
        const productData = data[0];
        const prices = data[1];
        let avs = [];
        this.includeAvailability = data[2].useAvailabilities || data[2].requireAvailability;
        if (this.includeAvailability) {
          avs = newIdsWithouConcept.map(id =>
            this.getProductAvailability(this.data.find(product => product.id === id))
          );
        }

        this.keyedMapData = {
          ...this.keyedMapData,
          ...newIds.reduce((acc, curr) => {
            const product = productData.filter(p => p.id === curr)[0];
            if (!product) {
              this.findError = true;
              this.errorMessage += 'No product found. ';
              throw new Error('No product found');
            }

            const price = prices.filter(p => p.productId === curr)[0];
            if (!price) {
              this.findError = true;
              this.errorMessage += 'No price found.';
              if (newIdsWithouConcept.includes(curr)) {
                throw new Error('No price found');
              }
            }

            const av = avs.filter(p => p.productId === curr)[0];
            if (!av && this.includeAvailability) {
              this.findError = true;
              this.errorMessage += 'No availability found. ';
              if (newIdsWithouConcept.includes(curr)) {
                throw new Error('No availability found');
              }
            }

            const item: ProductTableRow = this.mapItem(curr, product, price, av);
            return {
              ...acc,
              [curr]: item
            };
          }, {})
        };
        this.readMappedDataFromCache();
      });
    this.subscriptions.push(subscription);
  }

  protected mapItem(
    id: string,
    product: Product,
    price: CustomerProductPrice,
    availability: Availability
  ): ProductTableRow {
    return {
      id,
      name: product.name,
      product,
      price,
      packageSize: product.extra.ar?.artfsgforp,
      availability,
      replacementId: product.extra.ar?.q_gung_filter
    };
  }
}
